import type { ReactNode } from 'react';
import { usePermission, type UserRole } from '@/hooks/usePermission';

interface RoleGateProps {
  /** A single role or a list of roles that satisfies the gate. */
  role: UserRole | readonly UserRole[];
  /** Rendered when the role check fails. Defaults to `null` (hide). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Render `children` only when the current user's role matches. Used for the
 * surfaces that have no canonical permission in the SCP-012 catalog (dashboard,
 * operator management, generic comments). For everything else prefer
 * `<RequirePermission>` so a permission removal takes effect in the UI without
 * a role change.
 */
export function RoleGate({ role, fallback = null, children }: RoleGateProps) {
  const { hasRole } = usePermission();
  const roles = Array.isArray(role) ? role : [role as UserRole];
  const allowed = roles.some((r) => hasRole(r));
  return <>{allowed ? children : fallback}</>;
}
